import React from 'react';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Page, Sort } from '@syncfusion/ej2-react-grids';
import { useSearchWriteoffsQuery } from '../../../../../services/WriteoffService';
import { useLookupNavigation } from './hooks';
import {
  patientAccountTemplate,
  lookupAmountTemplate,
  lookupStatusTemplate,
  serviceDateTemplate,
  createdDateTemplate,
} from './templates';

interface WriteoffLookupGridProps {
  company: string;
  center: string;
  searchTerm: string;
}

export const WriteoffLookupGrid: React.FC<WriteoffLookupGridProps> = ({ company, center, searchTerm }) => {
  const { handleRowSelected } = useLookupNavigation(company, center);

  // Use RTK Query to fetch search results
  const { data = [], isLoading, isError, error } = useSearchWriteoffsQuery(
    { company, center, searchTerm },
    { skip: !searchTerm || searchTerm.length < 3 }
  );

  // Show message if search term is too short
  if (!searchTerm || searchTerm.length < 3) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="info">
          Enter at least 3 characters to search for writeoffs by patient name, account number, or case ID.
        </Alert>
      </Box>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Searching writeoffs...</Typography>
      </Box>
    );
  }

  // Show error state
  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error searching writeoffs: {(error as any)?.data?.message || 'Unknown error'}
        </Alert>
      </Box>
    );
  }

  // Show empty state
  if (data.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="info">
          No writeoffs found matching "{searchTerm}"
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ overflow: 'hidden', borderRadius: 2, border: '1px solid #e2e8f0' }}>
      <GridComponent
        dataSource={data}
        allowPaging={true}
        allowSorting={true}
        pageSettings={{ pageSize: 10, pageSizes: [10, 20, 50, 100] }}
        height="500px"
        gridLines="Both"
        rowHeight={40}
        rowSelected={handleRowSelected}
      >
        <ColumnsDirective>
          <ColumnDirective field="caseId" headerText="Case ID" width="120" />
          <ColumnDirective
            field="patientName"
            headerText="Patient / Account"
            width="200"
            template={patientAccountTemplate}
          />
          <ColumnDirective
            field="amount"
            headerText="Amount"
            width="120"
            template={lookupAmountTemplate}
            textAlign="Right"
          />
          <ColumnDirective
            field="status"
            headerText="Status"
            width="130"
            template={lookupStatusTemplate}
          />
          <ColumnDirective field="payerName" headerText="Payer" width="150" />
          <ColumnDirective
            field="serviceDate"
            headerText="Service Date"
            width="130"
            template={serviceDateTemplate}
          />
          <ColumnDirective
            field="createdDate"
            headerText="Created"
            width="130"
            template={createdDateTemplate}
          />
        </ColumnsDirective>
        <Inject services={[Page, Sort]} />
      </GridComponent>
    </Box>
  );
};

export default WriteoffLookupGrid;
